import {
  collection, doc, getDoc, setDoc, deleteDoc, query,
  onSnapshot, addDoc, where, updateDoc,
  writeBatch, serverTimestamp, getDocs,
  arrayUnion 
} from 'firebase/firestore';
import { 
    db, 
    sendGuestMeetingInvitesCallable, 
    markMeetingNotificationsAsReadCallable,
    proposeAgendaItemCallable,
    reviewAgendaItemCallable,
    createTranslationJobCallable,
    getTranslationJobDataCallable,
    submitTranslationCallable
} from './firebase'; 
import { generateId } from './utils';

// ========================================================================
// Meetings (i18n-ANPASSUNG)
// ========================================================================

const getMeetings = (currentCompanyId, currentUserId, callback) => {
  if (!currentUserId) return () => {}; 
  try {
    const indexColRef = collection(db, `userMeetingIndex/${currentUserId}/meetings`);
    const q = query(indexColRef, where('isExternal', '==', false)); 

    const unsubscribe = onSnapshot(q, {
      next: (querySnapshot) => {
        const meetingsList = querySnapshot.docs.map(doc => ({
            id: doc.id,
            title: doc.data().meetingTitle, 
            date: doc.data().meetingDate,   
            ...doc.data(),
            isExternal: false 
        }));
        callback(meetingsList);
      },
      error: (error) => {
        console.error("Error loading internal meetings (from Index):", error);
        callback([]); 
      }
    });
    return unsubscribe;
  } catch (error) {
    console.error("Error setting up internal meeting listener (from Index):", error);
    return () => {};
  }
};

const getCrossCompanyMeetings = (currentUserId, callback) => {
    if (!currentUserId) return () => {};
    try {
        const indexColRef = collection(db, `userMeetingIndex/${currentUserId}/meetings`);
        const q = query(indexColRef, where('isExternal', '==', true)); 

        const unsubscribe = onSnapshot(q, {
          next: (querySnapshot) => {
            const meetingsList = querySnapshot.docs.map(doc => ({
                id: doc.id, 
                title: doc.data().meetingTitle, 
                date: doc.data().meetingDate,   
                ...doc.data() 
            }));
            callback(meetingsList);
          },
          error: (error) => {
            console.error("Error loading cross-company meetings:", error);
            callback([]);
          }
        });
        return unsubscribe;

    } catch (error) {
        console.error("Error setting up cross-company meeting listener:", error);
        return () => {};
    }
};

const getMeetingMaster = async (companyId, meetingId) => {
  if (!companyId || !meetingId) {
    console.error("getMeetingMaster failed: Missing companyId or meetingId");
    return null;
  }
  try {
    const meetingDocRef = doc(db, `companies/${companyId}/meetings`, meetingId);
    const meetingDoc = await getDoc(meetingDocRef);
    if (meetingDoc.exists()) {
      return { id: meetingDoc.id, ...meetingDoc.data() };
    } else {
      console.warn(`Meeting Master ${meetingId} not found in company ${companyId}`);
      return null;
    }
  } catch (error) {
    console.error(`Error fetching meeting master ${meetingId} from ${companyId}:`, error);
    return null;
  }
};

const getMeetingContent = async (companyId, meetingId, langCode) => {
  if (!companyId || !meetingId || !langCode) {
    console.error("getMeetingContent failed: Missing companyId, meetingId, or langCode");
    return null;
  }
  try {
    const contentDocRef = doc(db, `companies/${companyId}/meetings/${meetingId}/content`, langCode);
    const contentDoc = await getDoc(contentDocRef);
    if (contentDoc.exists()) {
      return contentDoc.data();
    } else {
      console.warn(`Meeting Content '${langCode}' for ${meetingId} not found.`);
      return null; 
    }
  } catch (error) {
    console.error(`Error fetching meeting content ${langCode} for ${meetingId}:`, error);
    return null;
  }
};


const saveMeeting = async (meeting, currentCompanyId, currentUserId, currentLang) => {
  if (!currentCompanyId || !currentUserId || !currentLang) {
    throw new Error("Missing company, user ID, or current language.");
  }
  
  try {
    const meetingDocRef = doc(db, `companies/${currentCompanyId}/meetings`, meeting.id);
    const contentDocRef = doc(db, `companies/${currentCompanyId}/meetings/${meeting.id}/content`, currentLang);
    
    // === 1. DATEN TRENNEN ===
    
    // A. STRUKTUR-Daten (Master-Dokument)
    const masterData = {
      id: meeting.id,
      date: meeting.date, 
      startTime: meeting.startTime,
      endTime: meeting.endTime,
      location: meeting.location,
      
      projectId: meeting.projectId || null,          
      topicSourceId: meeting.topicSourceId || null,  
      visibility: meeting.visibility || 'internal',  

      allParticipants: meeting.allParticipants,
      participantUids: meeting.allParticipants.filter(p => p.uid).map(p => p.uid),
      moderatorUid: meeting.moderatorUid, 
      creatorUserId: meeting.creatorUserId || currentUserId,
      status: meeting.status || 'forecast',
      sequentialNumber: meeting.sequentialNumber || null,
      lastModifiedBy: currentUserId,
      
      originalLanguage: meeting.originalLanguage || 'de',
      availableLanguages: meeting.availableLanguages || ['de'],
      
      // Agenda-Struktur (UPDATE: Gäste mit speichern!)
      agenda_structure: (meeting.agenda || []).map(item => {
          const cleanSubPoints = (items) => {
              if (!Array.isArray(items)) return [];
              return items.map(sub => ({
                  id: sub.id,
                  createdBy: sub.createdBy,
                  // WICHTIG: Gäste auch in Unterpunkten speichern
                  guests: sub.guests || [],
                  guestUids: sub.guestUids || [],
                  subPoints: cleanSubPoints(sub.subPoints) 
              }));
          };
          return {
              id: item.id,
              createdBy: item.createdBy,
              // WICHTIG: Gäste hier speichern, damit sie persistiert werden
              guests: item.guests || [],
              guestUids: item.guestUids || [],
              subPoints: cleanSubPoints(item.subPoints)
          };
      })
    };

    // B. INHALTS-Daten
    const contentData = {
        title: meeting.title || ""
    };
    
    const addAgendaContent = (items) => {
        if (!Array.isArray(items)) return;
        items.forEach(item => {
            contentData[`item_${item.id}_desc`] = item.description || "";
            contentData[`item_${item.id}_notes`] = item.notes || "";
            if (item.subPoints) {
                addAgendaContent(item.subPoints);
            }
        });
    };
    addAgendaContent(meeting.agenda || []);
    
    
    // === 2. BATCH-WRITE ===
    const batch = writeBatch(db);
    
    batch.set(meetingDocRef, masterData, { merge: true });
    batch.set(contentDocRef, contentData, { merge: true });
    
    batch.update(meetingDocRef, {
        availableLanguages: arrayUnion(currentLang) 
    });
    
    await batch.commit();

  } catch (error) {
    console.error("Error saving meeting (i18n):", error);
    throw error;
  }
};


const deleteMeeting = async (id, currentCompanyId) => {
  if (!currentCompanyId) throw new Error("Missing company ID.");
  try {
    const meetingDocRef = doc(db, `companies/${currentCompanyId}/meetings`, id);
    console.warn("Löschen ist unvollständig! 'content', 'polls' etc. müssen via Backend gelöscht werden.");
    await deleteDoc(meetingDocRef);
  } catch (error) {
    console.error("Error deleting meeting:", error);
    throw error;
  }
};

const duplicateMeeting = async (companyId, meetingToDuplicate, includeCompletedTasks, currentTasksState) => {
  console.error("DUPLICATE MEETING IST FÜR i18n NOCH NICHT IMPLEMENTIERT!");
  throw new Error("Duplizieren ist für die neue Datenstruktur noch nicht implementiert.");
};


const sendGuestMeetingInvites = async (meeting, validityHours = 48) => {
    try {
        if (typeof meeting !== 'object' || !meeting || !meeting.id) {
            throw new Error("Invalid meeting data provided to sendGuestMeetingInvites.");
        }
        const result = await sendGuestMeetingInvitesCallable({ meeting, validityHours });
        if (result && result.data && typeof result.data.success === 'boolean') {
            return result.data;
        } else {
            console.error("Unexpected response structure from sendGuestMeetingInvitesCallable:", result);
            return { success: false, message: "Unerwartete Antwort von der Cloud Function." };
        }
    } catch (error) {
        console.error("Error calling sendGuestMeetingInvitesCallable:", error);
        return { success: false, message: error.message || "Unbekannter Fehler beim Aufruf der Cloud Function." };
    }
};

const updateParticipantStatus = async (companyId, meetingId, participantId, newStatus) => {
  if (!companyId || !meetingId || !participantId || !newStatus) {
    throw new Error("Fehlende Daten für das Status-Update.");
  }
  const meetingDocRef = doc(db, `companies/${companyId}/meetings`, meetingId);
  
  try {
    const meetingDoc = await getDoc(meetingDocRef);
    if (!meetingDoc.exists()) {
      throw new Error("Protokoll nicht gefunden.");
    }
    
    const meetingData = meetingDoc.data();
    const currentParticipants = meetingData.allParticipants || [];
    
    let participantFound = false;
    
    const updatedParticipants = currentParticipants.map(p => {
      if (p.id === participantId) {
        participantFound = true;
        return { ...p, currentStatus: newStatus };
      }
      return p;
    });
    
    if (!participantFound) {
      throw new Error("Teilnehmer nicht in diesem Protokoll gefunden.");
    }
    
    await updateDoc(meetingDocRef, {
      allParticipants: updatedParticipants
    });
    
    return updatedParticipants; 
    
  } catch (error) {
    console.error("Fehler in updateParticipantStatus:", error);
    throw error; 
  }
};

// ========================================================================
// Tasks 
// ========================================================================

const getTasks = (currentCompanyId, currentUserId, userRole, callback) => {
    if (!currentCompanyId || !currentUserId) return () => {};
    try {
        const tasksColRef = collection(db, `companies/${currentCompanyId}/tasks`);
        const q = query(tasksColRef);
        const unsubscribe = onSnapshot(q, {
          next: (querySnapshot) => {
            const tasksList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log(`[FirestoreService - getTasks] Loaded ${tasksList.length} tasks for company ${currentCompanyId}.`);
            callback(tasksList);
          },
          error: (error) => {
            console.error("Fehler beim Laden der Aufgaben:", error);
            callback([]);
          }
        });
        return unsubscribe;
    } catch (error) {
        console.error("Fehler beim Einrichten des Task-Listeners:", error);
        return () => {};
    }
};

const saveTask = async (taskData, currentCompanyId, currentUserId) => {
    if (!currentCompanyId || !currentUserId) throw new Error("Missing company or user ID.");
    try {
        const taskId = taskData.id || generateId();
        const taskDocRef = doc(db, `companies/${currentCompanyId}/tasks`, taskId);
        const dataToSave = {
          ...taskData,
          id: taskId, 
          lastModifiedBy: currentUserId,
         };
         if (typeof dataToSave.isCompleted !== 'boolean') {
             console.warn(`[FirestoreService - saveTask] Task ${taskId} wird gespeichert, aber isCompleted ist nicht boolean (${typeof dataToSave.isCompleted}). Setze auf false.`);
             dataToSave.isCompleted = false;
         }
        await setDoc(taskDocRef, dataToSave, { merge: true });
    } catch (error) {
        console.error("Error saving task:", error);
        throw error;
    }
};

const deleteTask = async (task, currentCompanyId) => {
    if (!currentCompanyId || !task || !task.id) throw new Error("Missing company ID or task data.");
    try {
        const taskDocRef = doc(db, `companies/${currentCompanyId}/tasks`, task.id);
        await deleteDoc(taskDocRef);
    } catch (error) {
        console.error("Error deleting task:", error);
        throw error;
    }
};

const getTasksForMeeting = async (companyId, meetingId) => {
  if (!companyId || !meetingId) return [];
  try {
    const tasksColRef = collection(db, `companies/${companyId}/tasks`);
    const q = query(tasksColRef, where('meetingId', '==', meetingId));
    const snapshot = await getDocs(q);
    const tasksList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log(`[getTasksForMeeting] Found ${tasksList.length} tasks for meeting ${meetingId} in company ${companyId}`);
    return tasksList;
  } catch (error) {
    console.error(`Error fetching tasks for external meeting ${meetingId}:`, error);
    return [];
  }
};


// ========================================================================
// Users & Profiles 
// ========================================================================

const getUserProfile = async (currentCompanyId, currentUserId) => {
  if (!currentCompanyId || !currentUserId) return null;
  try {
    const companyDocRef = doc(db, `companies/${currentCompanyId}`);
    const userDocRef = doc(db, `companies/${currentCompanyId}/users/${currentUserId}`);
    const [companyDoc, userDoc] = await Promise.all([
        getDoc(companyDocRef),
        getDoc(userDocRef)
    ]);
    const companyName = companyDoc.exists ? companyDoc.data().name : currentCompanyId;
    if (!userDoc.exists()) return null;
    
    const profileData = userDoc.data();
    if (!profileData.preferredLanguage) {
        profileData.preferredLanguage = 'de'; 
    }
    
    return { id: userDoc.id, ...profileData, companyName: companyName };
    
  } catch (error) {
    console.error("Error loading user profile:", error);
    return null;
  }
};

const saveUserProfile = async (currentCompanyId, currentUserId, profileData) => {
  if (!currentCompanyId || !currentUserId || typeof profileData !== 'object' || profileData === null) {
      throw new Error("Missing company ID, user ID, or invalid profile data.");
  }
  try {
    const userDocRef = doc(db, `companies/${currentCompanyId}/users/${currentUserId}`);
    await setDoc(userDocRef, profileData, { merge: true });
  } catch (error) {
    console.error("Error saving user profile:", error);
    throw error;
  }
};

const getCompanyUsers = (currentCompanyId, callback) => {
    if (!currentCompanyId) return () => {};
    try {
        const usersColRef = collection(db, `companies/${currentCompanyId}/users`);
        const q = query(usersColRef);
        const unsubscribe = onSnapshot(q, {
          next: (querySnapshot) => {
            const usersList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                uid: doc.id, 
                ...doc.data()
            }));
            callback(usersList);
          },
          error: (error) => {
            console.error("Error loading company users:", error);
            callback([]);
          }
        });
        return unsubscribe;
    } catch (error) {
        console.error("Error setting up company users listener:", error);
        return () => {};
    }
};

// ========================================================================
// Teams 
// ========================================================================

const getTeams = (currentCompanyId, callback) => {
  if (!currentCompanyId) return () => {};
  try {
    const teamsColRef = collection(db, `companies/${currentCompanyId}/teams`);
    const q = query(teamsColRef);
    const unsubscribe = onSnapshot(q, {
      next: (querySnapshot) => {
        const teamsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(teamsList);
      },
      error: (error) => {
        console.error("Error loading teams:", error);
        callback([]);
      }
    });
    return unsubscribe;
  } catch (error) {
    console.error("Error setting up teams listener:", error);
    return () => {};
  }
};

const saveTeam = async (team, currentCompanyId) => {
  if (!currentCompanyId || typeof team !== 'object' || !team || !team.id) {
      throw new Error("Missing company ID or invalid/missing team data/ID.");
  }
  try {
    const teamDocRef = doc(db, `companies/${currentCompanyId}/teams`, team.id);
    await setDoc(teamDocRef, team, { merge: true });
  } catch (error) {
    console.error("Error saving team:", error);
    throw error;
  }
};

// ========================================================================
// Company Registration 
// ========================================================================
const getCompanyRegistrationRequests = (callback) => {
  try {
    const requestsColRef = collection(db, 'companyRegistrationRequests');
    const q = query(requestsColRef);
    const unsubscribe = onSnapshot(q, {
      next: (querySnapshot) => {
        const requestsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(requestsList);
      },
      error: (error) => {
        console.error("Error loading company registration requests:", error);
        callback([]); 
      }
    });
    return unsubscribe;
  } catch (error) {
    console.error("Error setting up company requests listener:", error);
    return () => {}; 
  }
};

const deleteCompanyRegistrationRequest = async (requestId) => { console.warn("deleteCompanyRegistrationRequest not implemented fully."); };
const requestCompanyRegistration = async (requestData) => { console.warn("requestCompanyRegistration is likely handled by a Cloud Function."); };


// ========================================================================
// Notifications 
// ========================================================================
const createNotification = async (userId, meeting) => {
    if (!userId || typeof userId !== 'string') {
        console.error("createNotification failed: Invalid userId provided.", userId);
        return;
    }
    if (!meeting || typeof meeting !== 'object' || !meeting.id || !meeting.title || !meeting.companyId) {
        console.error("createNotification failed: Invalid meeting data provided.", meeting);
        return;
    }
    try {
        const notificationColRef = collection(db, `userNotifications/${userId}/notifications`);
        await addDoc(notificationColRef, {
            meetingId: meeting.id,
            meetingTitle: meeting.title, 
            companyId: meeting.companyId,
            isExternal: meeting.isExternal || false,
            type: 'forecast', 
            isRead: false,
            createdAt: serverTimestamp(),
        });
        console.log(`Notification created for user ${userId} regarding meeting ${meeting.id}`);
    } catch (error) {
        console.error(`Error creating notification for user ${userId} (Meeting ID: ${meeting.id}):`, error.code, error.message);
    }
};

const getUserNotifications = (currentUserId, callback) => {
    if (!currentUserId) return () => {};
    try {
        const notificationsColRef = collection(db, `userNotifications/${currentUserId}/notifications`);
        const q = query(notificationsColRef, where('isRead', '==', false));
        const unsubscribe = onSnapshot(q, {
          next: (querySnapshot) => {
            const notificationsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log(`[Notifications] Received ${notificationsList.length} unread notifications for user ${currentUserId}`);
            callback(notificationsList);
          },
          error: (error) => {
            console.error(`Error loading notifications for user ${currentUserId}:`, error.code, error.message);
            callback([]);
          }
        });
        return unsubscribe;
    } catch (error) {
        console.error(`Error setting up notifications listener for user ${currentUserId}:`, error);
        return () => {};
    }
};

const markNotificationAsRead = async (userId, notificationId) => {
    if (!userId || !notificationId) return;
    try {
        const notificationDocRef = doc(db, `userNotifications/${userId}/notifications`, notificationId);
        await updateDoc(notificationDocRef, { isRead: true });
         console.log(`Notification ${notificationId} marked as read for user ${userId}`);
    } catch (error) {
        console.error(`Error marking notification ${notificationId} as read for user ${userId}:`, error);
    }
};

const markNotificationsForMeetingAsRead = async (userId, meetingId) => {
    if (!userId || !meetingId) return;
    try {
        const result = await markMeetingNotificationsAsReadCallable({ userId, meetingId });
        if (result.data?.success) {
            console.log(`[Notifications] Successfully called CF to mark notifications as read for meeting ${meetingId}.`);
        } else {
             console.error(`[Notifications] CF returned error for meeting ${meetingId}:`, result.data?.message);
        }
    } catch (error) {
         console.error(`Error marking notifications as read for meeting ${meetingId}:`, error);
    }
};

// ========================================================================
// Agenda Proposals 
// ========================================================================

const getAgendaProposals = (companyId, meetingId, callback) => {
  if (!companyId || !meetingId) return () => {};
  try {
    const proposalsColRef = collection(db, `companies/${companyId}/meetings/${meetingId}/agendaProposals`);
    const q = query(proposalsColRef); 

    const unsubscribe = onSnapshot(q, {
      next: (querySnapshot) => {
        const proposalsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(proposalsList);
      },
      error: (error) => {
        console.error(`Error loading agenda proposals for meeting ${meetingId}:`, error);
        callback([]);
      }
    });
    return unsubscribe;
  } catch (error) {
    console.error("Error setting up agenda proposals listener:", error);
    return () => {};
  }
};

const proposeAgendaItem = async (companyId, meetingId, description) => {
  if (!companyId || !meetingId || !description) {
    throw new Error("Fehlende Daten für den Vorschlag.");
  }
  try {
    const result = await proposeAgendaItemCallable({ companyId, meetingId, description });
    return result.data; 
  } catch (error) {
    console.error("Error calling proposeAgendaItemCallable:", error);
    throw new Error(error.message || "Vorschlag konnte nicht gesendet werden.");
  }
};

const reviewAgendaItem = async (companyId, meetingId, proposalId, action) => {
  if (!companyId || !meetingId || !proposalId || !action) {
    throw new Error("Fehlende Daten für die Prüfung des Vorschlags.");
  }
  try {
    const result = await reviewAgendaItemCallable({ companyId, meetingId, proposalId, action });
    return result.data; 
  } catch (error) {
    console.error("Error calling reviewAgendaItemCallable:", error);
    throw new Error(error.message || "Vorschlag konnte nicht geprüft werden.");
  }
};

// ========================================================================
// === NEUE FUNKTIONEN FÜR i18n-Übersetzer-Portal ===
// ========================================================================

const createTranslationJob = async (companyId, meetingId, targetLanguage, translatorEmail) => {
    if (!companyId || !meetingId || !targetLanguage || !translatorEmail) {
        throw new Error("Fehlende Daten für den Übersetzungsauftrag.");
    }
    try {
        const result = await createTranslationJobCallable({ companyId, meetingId, targetLanguage, translatorEmail });
        return result.data; 
    } catch (error) {
        console.error("Error calling createTranslationJobCallable:", error);
        throw new Error(error.message || "Auftrag konnte nicht erstellt werden.");
    }
};

const getTranslationJobData = async (token) => {
    if (!token) {
        throw new Error("Token fehlt.");
    }
    try {
        const result = await getTranslationJobDataCallable({ token });
        if (result.data?.success) {
            return result.data.jobData; 
        } else {
            throw new Error(result.data?.message || "Unbekannter Fehler beim Laden der Auftragsdaten.");
        }
    } catch (error) {
        console.error("Error calling getTranslationJobDataCallable:", error);
        throw new Error(error.message || "Auftragsdaten konnten nicht geladen werden.");
    }
};

const submitTranslation = async (token, translationContent) => {
    if (!token || !translationContent) {
        throw new Error("Token oder Übersetzungsdaten fehlen.");
    }
    try {
        const result = await submitTranslationCallable({ token, translationContent });
        return result.data; 
    } catch (error) {
        console.error("Error calling submitTranslationCallable:", error);
        throw new Error(error.message || "Übersetzung konnte nicht eingereicht werden.");
    }
};


export {
  getMeetings,          
  saveMeeting,          
  getMeetingMaster,     
  getMeetingContent,    
  getCrossCompanyMeetings,
  deleteMeeting,
  duplicateMeeting,
  getUserProfile,
  saveUserProfile,
  getTasks,
  saveTask,
  deleteTask,
  getTasksForMeeting, 
  getCompanyUsers,
  getTeams,
  saveTeam,
  requestCompanyRegistration,
  getCompanyRegistrationRequests,
  deleteCompanyRegistrationRequest,
  sendGuestMeetingInvites,
  createNotification,
  getUserNotifications,
  markNotificationAsRead, 
  markNotificationsForMeetingAsRead, 
  updateParticipantStatus,
  getAgendaProposals,
  proposeAgendaItem,
  reviewAgendaItem,
  createTranslationJob,
  getTranslationJobData,
  submitTranslation
};